import { useState, useEffect, useRef } from 'react';
import { Button, Flex, Text } from '@dodilio/ui-design-lib';
import { useNavigate } from 'react-router-dom';
import { auth, hasOwnSession, myOrganizations as listMine, restoreSession, signIn, signOut } from '../../auth';
import { useSetAtom } from 'jotai';
import { ContinueAs } from '../../components/ContinueAs';
import { confirmAs, confirmedAs, signedInAtom, whoFromToken, type Who } from '../../session';
import { CreateOrgLayout } from './layout';
import { CreateOrgForm } from './form';
import { PageMeta } from '../../components/PageMeta';
import { leaveSignup } from '../../leave';
import { tabStore } from '../../tabStore';
import { readClientInvite } from '../../clientInvite';
import type { MyOrganization } from '../../types/register';

const REDIRECT_KEY = 'dodil_signup_auth_redirect';

type Notice = 'sign-in' | 'existing' | 'unchecked';

/** Their organizations, or null when iam couldn't say (never read that as "none"). */
const myOrganizations = (): Promise<MyOrganization[] | null> =>
    listMine().catch(() => null);

export const CreateOrg = () => {
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);
    // Signed in but couldn't be confirmed, an existing account being handed on,
    // or their organizations couldn't be read.
    const [notice, setNotice] = useState<Notice | null>(null);
    // `?new=1` from someone who already has organizations: ask before making another.
    const [already, setAlready] = useState<MyOrganization[] | null>(null);
    // A session this tab's signup didn't start: who it is, before anything is done for them.
    const [confirm, setConfirm] = useState<{ who: Who; go: () => void } | null>(null);
    const setSignedIn = useSetAtom(signedInAtom);

    // Once only: the first authenticate() spends Keycloak's sign-in code, so a
    // second run (StrictMode) would find no session, or throw on a second init.
    const started = useRef(false);
    useEffect(() => {
        if (started.current) return;
        started.current = true;
        let cancelLeave = () => {};
        const decide = async () => {
            // Started here (the code, or a sign-in this tab asked for), or found already in the browser.
            const ours = hasOwnSession() || /[#&?]code=/.test(window.location.hash + window.location.search);
            const state = await restoreSession().catch(() => 'unknown' as const);
            // Keycloak didn't answer: not "signed out", so no sign-in redirect loop.
            if (state === 'unknown') { setNotice('unchecked'); return; }
            if (state !== 'signed-in') {
                if (!tabStore.get(REDIRECT_KEY)) {
                    tabStore.set(REDIRECT_KEY, '1');
                    // Back to this very address, so `?new=1` survives the round trip.
                    await signIn(window.location.href);
                    return;
                }
                // The sign-in came back without a session. Not the sign-up page: they may well
                // have an account. Say so and offer both.
                tabStore.remove(REDIRECT_KEY);
                setNotice('sign-in');
                return;
            }
            tabStore.remove(REDIRECT_KEY);
            const who = whoFromToken(auth.getAccessToken()) ?? {};
            setSignedIn({ who });
            if (!ours && !confirmedAs(who)) {
                setConfirm({ who, go: () => { confirmAs(who); setConfirm(null); onward().catch(failed); } });
                return;
            }
            await onward();
        };

        // Signed in, and it's them: what they have decides what's next.
        const onward = async () => {
            // `?new=1`: they chose to make another organization (for an invitation, or from a partner page).
            const asked = new URLSearchParams(window.location.search).has('new');
            const invite = !!readClientInvite();
            if (!asked) {
                // No organization in the token doesn't make the account new: Google sign-up with
                // an account that exists, or a silent sign-in that named no organization, both
                // come back like that. Ask iam; an existing account is handed on, never asked
                // to "add an organization" it didn't ask for.
                const mine = auth.hasOrganization() ? [] : await myOrganizations();
                if (mine === null) { setNotice('unchecked'); return; }
                if (auth.hasOrganization() || mine.length) {
                    // Already has an organization: accept the invitation for one of them.
                    if (invite) { navigate('/client-invite'); return; }
                    if (auth.hasOrganization()) { leaveSignup(); return; }
                    setNotice('existing');
                    // The destination signs them in to their organization itself.
                    cancelLeave = leaveSignup(1200);
                    return;
                }
            } else if (!invite) {
                // They asked for a new one, but a page that sent them may have been wrong about
                // them having none (2026-10-29: a partner page read an error as "no organizations"
                // and they re-created theirs). Ask before the form. Not after an invitation: its
                // page listed their organizations and they chose a new one.
                const mine = await myOrganizations();
                if (mine === null) { setNotice('unchecked'); return; }
                if (mine.length) { setAlready(mine); return; }
            }
            setReady(true);
        };
        const failed = (e: unknown) => {
            console.warn('[register] org step', e);
            setNotice('unchecked');
        };
        decide().catch(failed);
        return () => cancelLeave();
    }, [navigate, setSignedIn]);

    if (confirm) {
        return (
            <CreateOrgLayout>
                <ContinueAs who={confirm.who} onContinue={confirm.go} onSomeoneElse={() => { void signOut(); }} />
            </CreateOrgLayout>
        );
    }

    if (notice) {
        return (
            <CreateOrgLayout>
                <Flex direction="column" gap="3">
                    {notice === 'existing' ? (
                        <Text size="2">You already have a Dodil account, so there&apos;s nothing to create. Taking you back…</Text>
                    ) : notice === 'unchecked' ? (
                        <>
                            <Text size="2" role="alert">We couldn&apos;t check your organizations just now, so nothing was created. Try again in a moment.</Text>
                            <Flex gap="3" wrap="wrap">
                                {/* A Keycloak instance starts once per page: trying again is a reload. */}
                                <Button onClick={() => window.location.reload()}>Try again</Button>
                            </Flex>
                        </>
                    ) : (
                        <>
                            <Text size="2">We couldn&apos;t confirm your sign-in. Sign in again to continue.</Text>
                            <Flex gap="3" wrap="wrap">
                                <Button onClick={() => { void signIn(window.location.href); }}>Sign in</Button>
                                <Button variant="soft" onClick={() => navigate('/')}>Create an account instead</Button>
                            </Flex>
                        </>
                    )}
                </Flex>
            </CreateOrgLayout>
        );
    }

    if (already && !ready) {
        const names = already.map((o) => o.displayName || o.organizationName);
        // Holding an organization's account stands in for having created one (iam counts creations).
        const holds = already.some((o) => o.isCreator);
        // An active partner may create more (iam exempts its members from the limit).
        const partner = already.some((o) => /ACTIVE$/i.test(o.partnerStatus ?? ''));
        const named = names.length === 1 ? names[0] : names.length === 2 ? `${names[0]} and ${names[1]}` : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
        return (
            <CreateOrgLayout>
                <Flex direction="column" gap="3">
                    <Text size="3" weight="medium">You already have {named}.</Text>
                    <Text size="2" color="gray">
                        {partner
                            ? 'As a Dodil partner you can create another organization, or use one you have.'
                            : holds
                                ? 'Use it to continue. Partners can create more organizations.'
                                : 'You’re a member there. You can also create an organization of your own.'}
                    </Text>
                    <Flex gap="3" wrap="wrap">
                        <Button onClick={() => leaveSignup()}>{already.length === 1 ? `Use ${names[0]}` : 'Use one of them'}</Button>
                        {/* iam allows one organization per person until they are a partner: someone who
                            holds one uses it (Seemo, 2026-10-29). A member only can create their first. */}
                        {(partner || !holds) && (
                            <Button variant="soft" onClick={() => setReady(true)}>{partner ? 'Create another' : 'Create my own'}</Button>
                        )}
                    </Flex>
                </Flex>
            </CreateOrgLayout>
        );
    }

    if (!ready) {
        return (
            <CreateOrgLayout>
                <Text size="2" color="gray" role="status">Checking your account…</Text>
            </CreateOrgLayout>
        );
    }

    return (
        <>
            <PageMeta
                title="Create your workspace · Dodil Cloud"
                description="Name your Dodil Cloud organization to get started."
            />
            <CreateOrgLayout>
                <CreateOrgForm />
            </CreateOrgLayout>
        </>
    );
};
